import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth, hasPagePermission } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, 
  Plus, 
  Trash2,
  Upload,
  Image as ImageIcon,
  Save
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

// Get full image URL - handles both base64 (legacy) and server URLs
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('data:')) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${backendUrl}${url}`;
  }
  return url;
};

export const NewAuditPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [headerSettings, setHeaderSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [auditNumber, setAuditNumber] = useState('');
  const [auditDate, setAuditDate] = useState(new Date().toISOString().split('T')[0]);
  const [items, setItems] = useState([]);
  
  const canEditAudits = true; // Všichni uživatelé mají přístup

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [headerRes, usersRes] = await Promise.all([
        api.get('/audit-header-settings'),
        api.get('/users/list')
      ]);
      setHeaderSettings(headerRes.data);
      setUsers(usersRes.data.filter(u => u.approved));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Chyba při načítání dat');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setItems([...items, {
      id: Date.now(),
      photo_url: null,
      photo_preview: null,
      location: '',
      finding: '',
      rectification: '',
      rectification_date: '',
      responsible_person_ids: []
    }]);
  };

  const removeItem = (itemId) => {
    setItems(items.filter(item => item.id !== itemId));
  };

  const updateItem = (itemId, field, value) => {
    setItems(items.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };

  const handlePhotoUpload = async (itemId, file) => {
    // Upload to server storage (Tigris)
    try {
      // Create preview immediately
      const preview = await createPreview(file);
      setItems(currentItems => currentItems.map(item => 
        item.id === itemId 
          ? { ...item, photo_url: null, photo_preview: preview } 
          : item
      ));
      
      // Upload to server
      const serverUrl = await uploadImageToServer(file);
      setItems(currentItems => currentItems.map(item => 
        item.id === itemId 
          ? { ...item, photo_url: serverUrl, photo_preview: preview } 
          : item
      ));
      toast.success('Fotografie nahrána');
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Chyba při nahrávání fotografie');
      setItems(currentItems => currentItems.map(item => 
        item.id === itemId 
          ? { ...item, photo_url: null, photo_preview: null } 
          : item
      ));
    }
  };

  // Upload image to server storage (Tigris) and return URL
  const uploadImageToServer = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    
    return response.data.url;
  };

  // Create preview from file for immediate display
  const createPreview = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.readAsDataURL(file);
    });
  };

  const handleSave = async () => {
    if (!auditNumber.trim()) {
      toast.error('Zadejte číslo inspekce');
      return;
    }
    if (!auditDate) {
      toast.error('Zadejte datum inspekce');
      return;
    }
    if (items.length === 0) {
      toast.error('Přidejte alespoň jeden nález');
      return;
    }
    
    // Validate items
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.location.trim()) {
        toast.error(`Nález ${i + 1}: Zadejte místo`);
        return;
      }
      if (!item.rectification.trim()) {
        toast.error(`Nález ${i + 1}: Zadejte nápravu`);
        return;
      }
      if (!item.rectification_date) {
        toast.error(`Nález ${i + 1}: Zadejte datum nápravy`);
        return;
      }
      if (!item.responsible_person_ids || item.responsible_person_ids.length === 0) {
        toast.error(`Nález ${i + 1}: Vyberte alespoň jednu zodpovědnou osobu`);
        return;
      }
    }
    
    try {
      setSaving(true);
      const auditData = {
        audit_number: auditNumber,
        audit_date: auditDate,
        items: items.map(item => ({
          photo_url: item.photo_url,
          location: item.location,
          finding: item.finding,
          rectification: item.rectification,
          rectification_date: item.rectification_date,
          responsible_person_ids: item.responsible_person_ids
        }))
      };
      
      // Debug: log photo data
      console.log('Saving audit with items:', auditData.items.map(i => ({
        location: i.location,
        photo_url_exists: !!i.photo_url,
        photo_url_length: i.photo_url?.length || 0
      })));
      
      const response = await api.post('/audits', auditData);
      toast.success('Inspekce vytvořena');
      navigate(`/audity/${response.data.id}`);
    } catch (error) {
      console.error('Error creating audit:', error);
      toast.error(error.response?.data?.detail || 'Chyba při vytváření inspekce');
    } finally {
      setSaving(false);
    }
  };

  if (!canEditAudits) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <p className="text-slate-500">Nemáte oprávnění vytvářet inspekce</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-5xl mx-auto">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/audity')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zpět na seznam
        </Button>

        {/* Header Info */}
        {headerSettings && (
          <div className="bg-slate-50 rounded-xl border p-4 mb-6">
            <div className="flex flex-wrap gap-4 text-sm text-slate-600">
              <span><strong>Dokument:</strong> {headerSettings.document_number}</span>
              <span><strong>Verze:</strong> {headerSettings.version}</span>
              <span><strong>Revize:</strong> {headerSettings.revision}</span>
              <span><strong>Účinný od:</strong> {headerSettings.effective_from}</span>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <h1 className="text-2xl font-bold text-slate-800 mb-6">Nová interní inspekce</h1>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <Label htmlFor="audit-number">Číslo inspekce *</Label>
              <Input
                id="audit-number"
                value={auditNumber}
                onChange={(e) => setAuditNumber(e.target.value)}
                placeholder="např. 2026-01"
                data-testid="audit-number-input"
              />
            </div>
            <div>
              <Label htmlFor="audit-date">Datum inspekce *</Label>
              <Input
                id="audit-date"
                type="date"
                value={auditDate}
                onChange={(e) => setAuditDate(e.target.value)}
                data-testid="audit-date-input"
              />
            </div>
          </div>

          {/* Items */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-800">Nálezy ({items.length})</h2>
            </div>

            {items.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed rounded-lg">
                <p className="text-slate-500 mb-4">Zatím žádné nálezy</p>
                <Button onClick={addItem} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Přidat první nález
                </Button>
              </div>
            ) : (
              <>
                {items.map((item, index) => (
                  <AuditItemForm
                    key={item.id}
                    item={item}
                    index={index}
                    users={users}
                    onUpdate={(field, value) => updateItem(item.id, field, value)}
                    onRemove={() => removeItem(item.id)}
                    onPhotoUpload={(file) => handlePhotoUpload(item.id, file)}
                  />
                ))}
                
                {/* Add item button at the end of list */}
                <Button 
                  onClick={addItem} 
                  variant="outline" 
                  className="w-full border-dashed border-2 h-14"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Přidat další nález
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Floating add button for mobile */}
        {items.length > 0 && (
          <button
            onClick={addItem}
            className="fixed bottom-24 right-6 md:hidden w-14 h-14 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-lg flex items-center justify-center z-50 transition-transform active:scale-95"
            title="Přidat nález"
          >
            <Plus className="w-7 h-7" />
          </button>
        )}

        {/* Save Button */}
        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={() => navigate('/audity')}>
            Zrušit
          </Button>
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="save-audit-button"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Ukládání...' : 'Uložit inspekci'}
          </Button>
        </div>
      </div>
    </Layout>
  );
};

// Audit Item Form Component
const AuditItemForm = ({ item, index, users, onUpdate, onRemove, onPhotoUpload }) => {
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onPhotoUpload(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      onPhotoUpload(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  return (
    <div className="border rounded-lg p-4 bg-slate-50">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-slate-800">Nález č. {index + 1}</h3>
        <Button variant="ghost" size="sm" onClick={onRemove} className="text-red-500 hover:text-red-700">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Photo Upload */}
        <div className="md:col-span-2">
          <Label>Fotografie</Label>
          <div 
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="mt-1 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors border-slate-300 hover:border-emerald-400"
          >
            <input 
              ref={fileInputRef}
              type="file" 
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
            {item.photo_preview || item.photo_url ? (
              <div className="relative">
                <img 
                  src={item.photo_preview || getImageUrl(item.photo_url)} 
                  alt="Nález" 
                  className="max-h-40 mx-auto rounded"
                />
                <p className="text-xs text-slate-500 mt-2">Klikněte nebo přetáhněte pro změnu</p>
              </div>
            ) : (
              <div className="py-4">
                <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Klikněte nebo přetáhněte fotografii</p>
              </div>
            )}
          </div>
        </div>

        {/* Location */}
        <div>
          <Label>Místo *</Label>
          <Input
            value={item.location}
            onChange={(e) => onUpdate('location', e.target.value)}
            placeholder="např. 5P suchý sklad"
          />
        </div>

        {/* Finding */}
        <div>
          <Label>Popis nálezu</Label>
          <Input
            value={item.finding}
            onChange={(e) => onUpdate('finding', e.target.value)}
            placeholder="např. rozsypaná surovina"
          />
        </div>

        {/* Rectification */}
        <div className="md:col-span-2">
          <Label>Náprava *</Label>
          <Textarea
            value={item.rectification}
            onChange={(e) => onUpdate('rectification', e.target.value)}
            placeholder="Co je potřeba udělat..."
            rows={2}
          />
        </div>

        {/* Rectification Date */}
        <div>
          <Label>Datum nápravy *</Label>
          <Input
            type="date"
            value={item.rectification_date}
            onChange={(e) => onUpdate('rectification_date', e.target.value)}
          />
        </div>

        {/* Responsible Person */}
        <div>
          <Label>Zodpovědné osoby * <span className="text-xs text-slate-500 font-normal">(vyberte jednu nebo více)</span></Label>
          <div className="mt-2 border rounded-lg p-3 max-h-32 overflow-y-auto space-y-2">
            {users.map(u => (
              <label key={u.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded">
                <input
                  type="checkbox"
                  checked={item.responsible_person_ids?.includes(u.id) || false}
                  onChange={(e) => {
                    const ids = item.responsible_person_ids || [];
                    if (e.target.checked) {
                      onUpdate('responsible_person_ids', [...ids, u.id]);
                    } else {
                      onUpdate('responsible_person_ids', ids.filter(id => id !== u.id));
                    }
                  }}
                  className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500"
                />
                <span className="text-sm">{u.name}</span>
              </label>
            ))}
          </div>
          {item.responsible_person_ids?.length > 0 && (
            <p className="text-xs text-slate-500 mt-1">
              Vybráno: {item.responsible_person_ids.length}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewAuditPage;
